// src/lib/onboarding-data-utils.ts
import {
  OnboardingStorageData,
  OnPerformanceData,
  OnSettingsData,
  OnDisapprovedData,
  OnEmptyAdGroupsData,
} from "@/types/onboarding";
import type {
  KeywordData,
  SearchTermData,
  QualityData,
  AccountInfo,
} from "@/types/metrics";

const DEBUG = process.env.NODE_ENV === "development";
// Updated fallback URL provided by the user for development convenience.
export const DEV_FALLBACK_WEB_APP_URL =
  "https://script.google.com/macros/s/AKfycbxGtytYgm2JaqxbXJ_yuLQ5c2xJFsggEFyWZMQKYJ5LENXrYH164IJj-e2G3AzbXMot/exec";

export const ONBOARDING_SHEET_TABS = {
  ACCOUNT: "account",
  KEYWORD80: "keyword80",
  SEARCH80: "search80",
  QUALITY: "quality",
  PERFORMANCE: "on_performance",
  SETTINGS: "on_settings",
  DISAPPROVED: "on_disapproved",
  EMPTY_AD_GROUPS: "on_empty_ad_groups",
} as const;

// NOTE: Using proper type transformers for data that has existing interfaces
const onboardingTransformers = {
  [ONBOARDING_SHEET_TABS.ACCOUNT]: (data: any[]): AccountInfo[] =>
    data.map((row) => ({
      accountName: String(row.accountName || ""),
      scriptVersion: String(row.scriptVersion || ""),
      ocid: String(row.ocid || ""),
      accountCID: String(row.accountCID || ""),
      currency: String(row.currency || "$"),
    })),
  [ONBOARDING_SHEET_TABS.KEYWORD80]: (data: any[]): KeywordData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      adGroup: String(row.adGroupName || row.adGroup || ""),
      keywordText: String(row.keyword || row.keywordText || ""),
      keywordMatchType: String(row.matchType || row.keywordMatchType || ""),
      impr: Number(row.impr || 0),
      clicks: Number(row.clicks || 0),
      cost: Number(row.cost || 0),
      conv: Number(row.conv || 0),
      value: Number(row.value || 0),
      allConv: Number(row.allConv || 0),
      allValue: Number(row.allValue || 0),
    })),
  [ONBOARDING_SHEET_TABS.SEARCH80]: (data: any[]): SearchTermData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      searchTerm: String(row.searchTerm || ""),
      searchMatchType: String(row.searchMatchType || ""),
      impr: Number(row.impr || 0),
      clicks: Number(row.clicks || 0),
      cost: Number(row.cost || 0),
      conv: Number(row.conv || 0),
      value: Number(row.value || 0),
      allConv: Number(row.allConv || 0),
      allValue: Number(row.allValue || 0),
      date: String(row.date || ""),
      CTR: Number(row.CTR || 0),
      CPC: Number(row.CPC || 0),
      CvR: Number(row.CvR || 0),
      ROAS: Number(row.ROAS || 0),
      CPA: Number(row.CPA || 0),
    })),
  [ONBOARDING_SHEET_TABS.QUALITY]: (data: any[]): QualityData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      adGroup: String(row.adGroupName || row.adGroup || ""),
      keywordText: String(row.keyword || ""),
      matchType: String(row.matchType || row.keywordMatchType || ""),
      qualityScore: row.qualityScore ? Number(row.qualityScore) : undefined,
      score: row.score ? Number(row.score) : undefined,
      expCTR: String(row.expCTR || ""),
      adRelevance: String(row.adRelevance || ""),
      landingExp: String(row.landingExp || ""),
    })),
  [ONBOARDING_SHEET_TABS.PERFORMANCE]: (data: any[]): OnPerformanceData[] => {
    console.log(
      "[Transformer] Processing performance data:",
      data.length,
      "rows",
    );
    return data.map((row, index) => {
      const result = {
        Campaign: String(row.Campaign || ""),
        ImprYesterday: Number(row.ImprYesterday || 0),
        ImprAvg13W: Number(row.ImprAvg13W || 0),
        ImprDrop13W: Number(row.ImprDrop13W || 0),
        ImprLevel13W: String(row.ImprLevel13W || ""),
        ImprAvg4W: Number(row.ImprAvg4W || 0),
        ImprDrop4W: Number(row.ImprDrop4W || 0),
        ImprLevel4W: String(row.ImprLevel4W || ""),
        CostYesterday: Number(row.CostYesterday || 0),
        CostAvg13W: Number(row.CostAvg13W || 0),
        CostDrop13W: Number(row.CostDrop13W || 0),
        CostLevel13W: String(row.CostLevel13W || ""),
        CostAvg4W: Number(row.CostAvg4W || 0),
        CostDrop4W: Number(row.CostDrop4W || 0),
        CostLevel4W: String(row.CostLevel4W || ""),
        ConvYesterday: Number(row.ConvYesterday || 0),
        ConvAvg13W: Number(row.ConvAvg13W || 0),
        ConvDrop13W: Number(row.ConvDrop13W || 0),
        ConvLevel13W: String(row.ConvLevel13W || ""),
        ConvAvg4W: Number(row.ConvAvg4W || 0),
        ConvDrop4W: Number(row.ConvDrop4W || 0),
        ConvLevel4W: String(row.ConvLevel4W || ""),
      };
      if (index === 0) {
        console.log("[Transformer] Sample transformed row:", result);
      }
      return result;
    });
  },
  [ONBOARDING_SHEET_TABS.SETTINGS]: (data: any[]): OnSettingsData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      campaignId: String(row.campaignId || ""),
      status: String(row.status || ""),
      channel: String(row.channel || ""),
      targetContentNetwork: Boolean(
        row.targetContentNetwork === true ||
          row.targetContentNetwork === "true",
      ),
      networkType: String(row.networkType || ""),
      geoTargetTypePos: row.geoTargetTypePos
        ? String(row.geoTargetTypePos)
        : undefined,
      impr: Number(row.impr || 0),
      clicks: Number(row.clicks || 0),
      cost: Number(row.cost || 0),
      conv: Number(row.conv || 0),
      value: Number(row.value || 0),
      tcpaTarget: row.tcpaTarget ? Number(row.tcpaTarget) : undefined,
      troasTarget: row.troasTarget ? Number(row.troasTarget) : undefined,
      lostBudget: row.lostBudget ? Number(row.lostBudget) : undefined,
    })),
  [ONBOARDING_SHEET_TABS.DISAPPROVED]: (data: any[]): OnDisapprovedData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      adGroupName: String(row.adGroupName || ""),
      adId: String(row.adId || ""),
      approvalStatus: String(row.approvalStatus || ""),
      policyTopics: String(row.policyTopics || "[]"),
    })),
  [ONBOARDING_SHEET_TABS.EMPTY_AD_GROUPS]: (
    data: any[],
  ): OnEmptyAdGroupsData[] =>
    data.map((row) => ({
      campaign: String(row.campaign || ""),
      campaignId: String(row.campaignId || ""),
      status: String(row.status || ""),
      adGroupName: String(row.adGroupName || ""),
      adgroupid: String(row.adgroupid || ""),
      adgroupstatus: String(row.adgroupstatus || ""),
    })),
} as const;

export async function fetchAllOnboardingData(
  dexieId: string,
  webAppUrl: string,
): Promise<OnboardingStorageData> {
  let effectiveWebAppUrl = webAppUrl;
  if (DEBUG && !effectiveWebAppUrl) {
    console.warn(
      "[Onboarding] No webAppUrl provided in dev mode. Using fallback URL.",
    );
    effectiveWebAppUrl = DEV_FALLBACK_WEB_APP_URL;
  }

  if (DEBUG)
    console.log("[ONBOARDING] Fetching all data for", {
      dexieId,
      webAppUrl: effectiveWebAppUrl,
    });
  console.log("[ONBOARDING] Starting fetch at:", new Date().toISOString());

  const storageData: Partial<OnboardingStorageData> = {
    dexieId,
    timestamp: new Date().toISOString(),
  };

  const fetchPromises = Object.values(ONBOARDING_SHEET_TABS).map(
    async (tabName) => {
      try {
        const response = await fetch(`${effectiveWebAppUrl}?tab=${tabName}`);
        if (!response.ok)
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const responseText = await response.text();
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          if (responseText.trim().toLowerCase().startsWith("success")) {
            if (DEBUG)
              console.warn(
                `[Onboarding] Received success string for tab ${tabName}. Treating as empty data.`,
              );
            data = [];
          } else {
            throw new Error(
              `Invalid JSON response for tab ${tabName}: ${responseText.substring(0, 100)}`,
            );
          }
        }

        if (!Array.isArray(data))
          throw new Error("Invalid data format - expected array");
        if (data.length === 1 && data[0]?.error) throw new Error(data[0].error);

        const transformer =
          onboardingTransformers[
            tabName as keyof typeof onboardingTransformers
          ];
        const transformedData = transformer ? transformer(data) : data;

        if (DEBUG)
          console.log(`[Onboarding] Loaded ${tabName}:`, {
            raw: data.length,
            transformed: transformedData.length,
          });

        return { tabName, data: transformedData, success: true };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Unknown error";
        if (DEBUG)
          console.error(
            `[Onboarding] Failed to fetch tab ${tabName}:`,
            errorMsg,
          );
        return { tabName, data: [], success: false, error: errorMsg };
      }
    },
  );

  const results = await Promise.all(fetchPromises);

  results.forEach((result) => {
    if (result.success) {
      storageData[result.tabName as keyof OnboardingStorageData] =
        result.data as any;
    }
  });

  return storageData as OnboardingStorageData;
}
